import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatPrice } from "../mock";
import PaintEdges from "../components/PaintEdges";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 10;

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | paid | failed | timeout
  const [amount, setAmount] = useState(null);
  const pollCount = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("failed");
      return;
    }
    let timer;
    const poll = async () => {
      if (pollCount.current >= MAX_POLLS) {
        setState("timeout");
        return;
      }
      pollCount.current += 1;
      try {
        const res = await axios.get(`${API}/payments/status/${sessionId}`);
        setAmount(res.data.amount);
        if (res.data.payment_status === "paid") {
          setState("paid");
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) {
          setState("failed");
          return;
        }
        timer = setTimeout(poll, 2000);
      } catch {
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <main className="relative overflow-hidden bg-gradient-to-b from-[#dff2fc] via-[#ecf9ee] to-[#fff6ea] min-h-[calc(100vh-76px)] flex items-center">
      <PaintEdges from="#1b9ad6" via="#7cc24a" to="#f7941e" opacity={0.5} />
      <div className="relative mx-auto max-w-lg w-full px-6">
        <div className="card-soft p-10 text-center">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto h-16 w-16 text-[#1b9ad6] animate-spin" />
              <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-800">
                Zahlung wird bestätigt ...
              </h1>
              <p className="mt-2 text-slate-500">Einen Moment bitte, wir prüfen deine Zahlung.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="mx-auto h-16 w-16 text-[#3f8f2a]" />
              <h1 className="mt-5 font-display text-3xl font-extrabold">
                <span className="text-[#1b7fc1]">Vielen</span> <span className="text-[#6fbf3f]">Dank!</span>
              </h1>
              <p className="mt-2 text-slate-600">
                Deine Zahlung {amount ? `über ${formatPrice(amount)} ` : ""}war erfolgreich. Wir
                kümmern uns um deine Bestellung!
              </p>
              <Link to="/produkte" className="btn-gradient-warm mt-6">
                Weiter stöbern <ArrowRight className="h-5 w-5" />
              </Link>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle className="mx-auto h-16 w-16 text-red-500" />
              <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-800">
                {state === "timeout" ? "Zahlung noch nicht bestätigt" : "Zahlung fehlgeschlagen"}
              </h1>
              <p className="mt-2 text-slate-500">
                {state === "timeout"
                  ? "Die Bestätigung dauert länger als erwartet. Prüfe später deinen Status."
                  : "Leider konnten wir deine Zahlung nicht abschließen."}
              </p>
              <Link to="/produkte" className="btn-gradient mt-6">
                Zurück zum Shop <ArrowRight className="h-5 w-5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export const PaymentCancel = () => (
  <main className="relative overflow-hidden bg-gradient-to-b from-[#dff2fc] to-white min-h-[calc(100vh-76px)] flex items-center">
    <div className="relative mx-auto max-w-lg w-full px-6">
      <div className="card-soft p-10 text-center">
        <XCircle className="mx-auto h-16 w-16 text-slate-400" />
        <h1 className="mt-5 font-display text-2xl font-extrabold text-slate-800">Zahlung abgebrochen</h1>
        <p className="mt-2 text-slate-500">Kein Problem – dein Warenkorb bleibt gespeichert.</p>
        <Link to="/produkte" className="btn-gradient mt-6">
          Zurück zum Shop <ArrowRight className="h-5 w-5" />
        </Link>
      </div>
    </div>
  </main>
);
